import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { adminUsersApi, invitationsApi } from '../../services/api'
import type {
  AssignSchoolRequest,
  CreateInvitationRequest,
  RoleAssignmentRequest,
  RoleChangeRequest,
} from '../../types/api'
import { parseApiError } from '@/lib/api-client'
import { notifyApiError } from '@/lib/error-handler'

export function useUpdateUser(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => adminUsersApi.updateUser(userId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
  })
}

export function useDisableUser(defaultUserId?: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (targetId?: number) => {
      const userId = targetId ?? defaultUserId
      if (!userId) {
        throw new Error('User id is required to disable a user')
      }
      return adminUsersApi.disableUser(userId)
    },
    onSuccess: (response, targetId) => {
      toast.success(response.message ?? 'User disabled')
      const userId = targetId ?? defaultUserId
      qc.invalidateQueries({ queryKey: ['users'] })
      if (userId) {
        qc.invalidateQueries({ queryKey: ['user', userId] })
      }
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified here.')
          return
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to perform this action.')
          return
        default:
          notifyApiError(error, 'Disable failed')
      }
    },
  })
}

export function useEnableUser(defaultUserId?: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (targetId?: number) => {
      const userId = targetId ?? defaultUserId
      if (!userId) {
        throw new Error('User id is required to enable a user')
      }
      return adminUsersApi.enableUser(userId)
    },
    onSuccess: (response, targetId) => {
      toast.success(response.message ?? 'User enabled')
      const userId = targetId ?? defaultUserId
      qc.invalidateQueries({ queryKey: ['users'] })
      if (userId) {
        qc.invalidateQueries({ queryKey: ['user', userId] })
      }
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified here.')
          return
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to perform this action.')
          return
        default:
          notifyApiError(error, 'Enable failed')
      }
    },
  })
}

export function useDeleteUser(defaultUserId?: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (targetId?: number) => {
      const userId = targetId ?? defaultUserId
      if (!userId) {
        throw new Error('User id is required to delete a user')
      }
      return adminUsersApi.deleteUser(userId)
    },
    onSuccess: (response, targetId) => {
      toast.success(response.message ?? 'User permanently deleted')
      const userId = targetId ?? defaultUserId
      qc.invalidateQueries({ queryKey: ['users'] })
      if (userId) {
        qc.invalidateQueries({ queryKey: ['user', userId] })
      }
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified here.')
          return
        case 'DELETE_REQUIRES_DISABLED':
          toast.error('Disable the account before performing a permanent delete.')
          return
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to perform this action.')
          return
        default:
          notifyApiError(error, 'Delete failed')
      }
    },
  })
}

export function useAssignSchool(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AssignSchoolRequest) => adminUsersApi.assignSchool(userId, data),
    onSuccess: () => {
      toast.success('Successfully assigned to school')
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified here.')
          return
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to perform this action.')
          return
        default:
          notifyApiError(error, 'School assignment failed')
      }
    },
  })
}

export function useAssignSchools(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (schoolIds: string[]) => {
      await Promise.all(
        schoolIds.map(schoolId => adminUsersApi.assignSchool(userId, { school_id: schoolId })),
      )
      return schoolIds
    },
    onSuccess: schoolIds => {
      toast.success(
        schoolIds.length > 1
          ? 'Successfully assigned to selected schools'
          : 'Successfully assigned to school',
      )
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified here.')
          return
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to perform this action.')
          return
        default:
          notifyApiError(error, 'School assignment failed')
      }
    },
  })
}

export function useRemoveSchool(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (schoolId: string) => adminUsersApi.removeSchool(userId, schoolId),
    onSuccess: () => {
      toast.success('Successfully removed from school')
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified here.')
          return
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to perform this action.')
          return
        default:
          notifyApiError(error, 'Failed to remove from school')
      }
    },
  })
}

export function useCreateInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInvitationRequest) => invitationsApi.createInvitation(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useResendInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => invitationsApi.resendInvitation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useRevokeInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => invitationsApi.revokeInvitation(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invitations'] })
    },
  })
}

export function useChangePrimaryRole(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RoleChangeRequest) => adminUsersApi.changeRole(userId, payload),
    onSuccess: response => {
      toast.success(response.message ?? 'Primary role updated')
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? "You do not have permission to change this user's role.")
          return
        case 'INVALID_ROLE':
          toast.error('Invalid role specified.')
          return
        case 'NO_CHANGE_NEEDED':
          toast.error('User already has this role.')
          return
        case 'NO_SCHOOL_ASSIGNMENT':
          toast.error('Select at least one school for this role.')
          return
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified.')
          return
        default:
          notifyApiError(error, 'Failed to change user role')
      }
    },
  })
}

export function useAssignAdditionalRole(userId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: RoleAssignmentRequest) => adminUsersApi.assignRole(userId, payload),
    onSuccess: response => {
      toast.success(response.message ?? 'Role assigned successfully')
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to assign this role.')
          return
        case 'INVALID_ROLE':
          toast.error('Invalid role specified.')
          return
        case 'NO_CHANGE_NEEDED':
          toast.error('User already has this role.')
          return
        case 'NO_SCHOOL_ASSIGNMENT':
          toast.error('Select at least one school for this role.')
          return
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified.')
          return
        default:
          notifyApiError(error, 'Failed to assign role')
      }
    },
  })
}

const formatRoleLabel = (role: string) =>
  role
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')

export function useRemoveRole(
  userId: number,
  options?: {
    getSuccessMessage?: (
      role: string,
      response: { success: true; message: string },
    ) => string | undefined
  },
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (role: string) => adminUsersApi.removeRole(userId, role),
    onSuccess: (response, variables) => {
      const customMessage = options?.getSuccessMessage?.(variables, response)
      const fallbackMessage = `${formatRoleLabel(variables)} role removed.`
      toast.success(customMessage ?? response.message ?? fallbackMessage)
      qc.invalidateQueries({ queryKey: ['users'] })
      qc.invalidateQueries({ queryKey: ['user', userId] })
    },
    onError: error => {
      const parsed = parseApiError(error)
      switch (parsed.code) {
        case 'PERMISSION_DENIED':
          toast.error(parsed.message ?? 'You do not have permission to remove this role.')
          return
        case 'CANNOT_REMOVE_LAST_ROLE':
          toast.error("Cannot remove the user's only role.")
          return
        case 'ROLE_NOT_FOUND':
          toast.error('User does not have this role.')
          return
        case 'SUPER_ADMIN_PROTECTED':
          toast.error('Super administrator accounts are protected and cannot be modified.')
          return
        default:
          notifyApiError(error, 'Failed to remove role')
      }
    },
  })
}
